import { type RefObject, useEffect, useRef, useState } from 'react';

import type { StudyDocumentThumbnail } from './studyApi';
import { MaterialIcon } from './StudyMaterialIcon';
import { pdfThumbnail } from './studyPdfThumbnail';

/**
 * Left thumbnail of a material card (Subject Materials tab, Materials library). An image
 * thumbnail is the signed page-1 PNG or photo. A PDF is drawn from page 1 with the lazy pdf.js
 * loader, only once the row is on screen, small, and once per document. Any failure, or no
 * thumbnail, shows the file-type icon. `alt` is empty: the row title already names the material.
 */

/**
 * Box sizes. `card` is the material card (owner feedback 2026-10-08: the thumbnail was too small):
 * 88px square on a phone, 120px from `sm`. `compact` is the earlier 56px / 72px row size.
 * `row` is the 48px Study Home Recent materials row, with a smaller corner so it stays square.
 */
export const MATERIAL_THUMBNAIL_SIZES = {
  card: 'h-[88px] w-[88px] sm:h-[120px] sm:w-[120px]',
  compact: 'h-14 w-14 sm:h-[72px] sm:w-[72px]',
  row: 'h-12 w-12 !rounded-[14px]',
} as const;

export type MaterialThumbnailSize = keyof typeof MATERIAL_THUMBNAIL_SIZES;

/** True once the element is on screen. Without IntersectionObserver it is always true. */
function useOnScreen(target: RefObject<Element>, enabled: boolean): boolean {
  const [onScreen, setOnScreen] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const element = target.current;
    if (!enabled || onScreen || !element || typeof IntersectionObserver === 'undefined')
      return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setOnScreen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, onScreen, target]);
  return onScreen;
}

export function StudyMaterialThumbnail({
  documentId,
  mimeType,
  thumbnail,
  size = 'card',
}: {
  documentId: string;
  mimeType: string;
  thumbnail: StudyDocumentThumbnail | null | undefined;
  size?: MaterialThumbnailSize;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const isPdf = thumbnail?.kind === 'pdf';
  const onScreen = useOnScreen(box, isPdf);
  const [pdfSrc, setPdfSrc] = useState<string | null>(null);
  // Failure is tied to one signed URL, so a re-signed URL after a list refresh is tried again.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = thumbnail?.url ?? null;
  const failed = url !== null && failedUrl === url;

  useEffect(() => {
    if (!isPdf || !onScreen || !url) return undefined;
    let active = true;
    pdfThumbnail(documentId, url).then(
      (src) => {
        if (active) setPdfSrc(src);
      },
      () => {
        if (active) setFailedUrl(url);
      },
    );
    return () => {
      active = false;
    };
  }, [documentId, isPdf, onScreen, url]);

  const src = failed ? null : thumbnail?.kind === 'image' ? thumbnail.url : pdfSrc;
  return (
    <span
      ref={box}
      data-testid="study-material-thumbnail"
      data-size={size}
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-hairline bg-wash-sky text-brand-sky ${MATERIAL_THUMBNAIL_SIZES[size]}`}
    >
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <MaterialIcon mimeType={mimeType} />
      )}
    </span>
  );
}
